import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Download,
    Building2,
    Users,
    TrendingUp,
    Receipt,
    BookOpen,
    FileSpreadsheet,
    Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

const Exports = () => {
    const { api } = useAuth();
    const [loading, setLoading] = useState({});

    const handleExport = async (type, params = {}) => {
        setLoading(prev => ({ ...prev, [type]: true }));
        
        try {
            const queryParams = new URLSearchParams(params).toString();
            const url = `/export/${type}${queryParams ? `?${queryParams}` : ''}`;
            
            const response = await api.get(url, { responseType: 'blob' });
            
            // Create download link
            const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            
            // Get filename from headers or generate one
            const contentDisposition = response.headers['content-disposition'];
            let filename = `export_${type}_${new Date().toISOString().split('T')[0]}.csv`;
            if (contentDisposition) {
                const match = contentDisposition.match(/filename=(.+)/);
                if (match) filename = match[1];
            }
            
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
            
            toast.success('Export téléchargé');
        } catch (error) {
            console.error('Export failed:', error);
            toast.error('Erreur lors de l\'export');
        } finally {
            setLoading(prev => ({ ...prev, [type]: false }));
        }
    };

    const exportOptions = [
        {
            id: 'companies',
            title: 'Entreprises',
            description: 'Exportez la liste complète des entreprises avec leurs informations',
            icon: Building2,
            color: 'text-blue-500',
            bgColor: 'bg-blue-500/10',
            fields: ['Raison sociale', 'SIRET', 'Adresse', 'Secteur', 'Commercial', 'Statut'],
        },
        {
            id: 'contacts',
            title: 'Contacts',
            description: 'Exportez tous les contacts avec leurs coordonnées',
            icon: Users,
            color: 'text-green-500',
            bgColor: 'bg-green-500/10',
            fields: ['Nom', 'Prénom', 'Email', 'Téléphone', 'Entreprise', 'Fonction'],
        },
        {
            id: 'opportunities',
            title: 'Opportunités',
            description: 'Exportez le pipeline commercial avec les montants et statuts',
            icon: TrendingUp,
            color: 'text-purple-500',
            bgColor: 'bg-purple-500/10',
            fields: ['Titre', 'Entreprise', 'Commercial', 'Étape', 'Montant', 'Statut'],
        },
        {
            id: 'expenses',
            title: 'Notes de frais',
            description: 'Exportez les notes de frais pour la comptabilité',
            icon: Receipt,
            color: 'text-orange-500',
            bgColor: 'bg-orange-500/10',
            fields: ['Date', 'Commercial', 'Catégorie', 'Montant', 'Client', 'Statut'],
        },
        {
            id: 'editorial-projects',
            title: 'Dossiers Magazine',
            description: 'Exportez les dossiers éditoriaux et leur progression',
            icon: BookOpen,
            color: 'text-emerald-500',
            bgColor: 'bg-emerald-500/10',
            fields: ['Titre', 'Entreprise', 'Magazine', 'Édition', 'Progression', 'Statut'],
        },
    ];

    return (
        <div className="space-y-6 animate-fade-in" data-testid="exports-page">
            {/* Header */}
            <div>
                <h1 className="text-2xl lg:text-3xl font-bold font-heading" data-testid="exports-title">Exports</h1>
                <p className="text-muted-foreground mt-1">
                    Téléchargez vos données au format CSV
                </p>
            </div>

            {/* Export Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {exportOptions.map((option) => {
                    const Icon = option.icon;
                    const isLoading = loading[option.id];
                    
                    return (
                        <Card key={option.id} className="card-hover">
                            <CardHeader>
                                <div className="flex items-start justify-between">
                                    <div className={`p-3 rounded-xl ${option.bgColor}`}>
                                        <Icon className={`w-6 h-6 ${option.color}`} />
                                    </div>
                                    <FileSpreadsheet className="w-5 h-5 text-muted-foreground" />
                                </div>
                                <CardTitle className="font-heading mt-4">{option.title}</CardTitle>
                                <CardDescription>{option.description}</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    <div>
                                        <p className="text-xs font-medium text-muted-foreground mb-2">
                                            Colonnes incluses :
                                        </p>
                                        <div className="flex flex-wrap gap-1">
                                            {option.fields.map((field) => (
                                                <span
                                                    key={field}
                                                    className="text-xs px-2 py-0.5 bg-muted rounded"
                                                >
                                                    {field}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                    
                                    <Button
                                        onClick={() => handleExport(option.id)}
                                        disabled={isLoading}
                                        className="w-full rounded-full"
                                        data-testid={`export-${option.id}-btn`}
                                    >
                                        {isLoading ? (
                                            <>
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                Export en cours...
                                            </>
                                        ) : (
                                            <>
                                                <Download className="w-4 h-4 mr-2" />
                                                Télécharger CSV
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            {/* Info */}
            <Card>
                <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                        <div className="p-3 rounded-xl bg-primary/10">
                            <FileSpreadsheet className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                            <h3 className="font-semibold font-heading">Format des exports</h3>
                            <p className="text-sm text-muted-foreground mt-1">
                                Tous les exports sont au format CSV (valeurs séparées par des virgules), 
                                compatible avec Excel, Google Sheets et la plupart des logiciels de tableur.
                                L'encodage utilisé est UTF-8 pour garantir l'affichage correct des caractères français.
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default Exports;
